import { API_BASE_URL, API_KEY } from "./useApiConfig";

export interface PendingRequest {
    key: string
    url: string
    http_method: string
    payload: Record<string, unknown>
    date_added_utc: string
    date_last_attempt_utc: string
    retry_count: number
    in_flight: boolean
}

export interface RequestQueueConfig {
    maxQueueSize?: number
    retentionDurationHours?: number
    requestTimeoutSecs?: number
    intervalSecs?: number
    retryLimit?: number
    maxDelayMsBetweenRequests?: number
}

export interface RequestQueue {
    addItem(item: Omit<PendingRequest, 'date_added_utc' | 'date_last_attempt_utc' | 'retry_count' | 'in_flight'>): void
    prepareQueue(queue: PendingRequest[]): PendingRequest[]
    processQueue(): void
    sendRequest(key: PendingRequest): Promise<void>
    start(): void
    stop(): void
}

const DEFAULT_CONFIG: Required<RequestQueueConfig> = {
    maxQueueSize: 25,
    retentionDurationHours: 168, // 7 days
    requestTimeoutSecs: 30,
    intervalSecs: 10,
    retryLimit: 3,
    maxDelayMsBetweenRequests: 2500,
}

const STORAGE_KEY = 'pendingRequests'

export const abortControllers = new Map<string, AbortController>()

function getQueue(): PendingRequest[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : []
    } catch (err) {
        console.error('[RequestQueue] Failed to read queue from storage', err)
        return []
    }
}

function saveQueue(queue: PendingRequest[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(queue))
    } catch (err) {
        console.error('[RequestQueue] Failed to persist queue to storage', err)
    }
}

function nowUtc(): string {
    return new Date().toISOString()
}

function isExpired(item: PendingRequest, retentionHours: number): boolean {
    const added = new Date(item.date_added_utc).getTime()
    const cutoff = Date.now() - retentionHours * 60 * 60 * 1000
    return added < cutoff
}

function isTimedOut(item: PendingRequest, timeoutSecs: number): boolean {
    const lastAttempt = new Date(item.date_last_attempt_utc).getTime()
    return Date.now() - lastAttempt > timeoutSecs * 1000
}

export function useRequestQueue(config: RequestQueueConfig = {}): RequestQueue {
    const resolved = { ...DEFAULT_CONFIG, ...config }
    let intervalId: ReturnType<typeof setInterval> | null = null

    function addItem(item: Omit<PendingRequest, 'date_added_utc' | 'date_last_attempt_utc' | 'retry_count' | 'in_flight'>): void {
        let queue = getQueue()
        let existing = queue.find(
            (r) => r.key === item.key
        )

        if (existing) {
            if (existing.in_flight) {
                const existingController = abortControllers.get(item.key)
                if (existingController) {
                    existingController.abort()
                    abortControllers.delete(item.key)
                }
                // remove item from queue (to re-add later)
                const index = queue.findIndex((r) => r.key === item.key)
                if (index !== -1) {
                    queue = [...queue.slice(0, index), ...queue.slice(index + 1)]
                    console.log(`[RequestQueue] ${item.key}: Removed in-flight request`)
                    existing = undefined
                }
            } else {
                Object.assign(existing, item, {
                    date_added_utc: nowUtc(),
                    date_last_attempt_utc: nowUtc(),
                    retry_count: 0,
                    in_flight: false,
                })
                // replace existing item in requestQueue
                console.log(`[RequestQueue] ${item.key}: Marked as not in-flight and incremented retry count`)
            }
        }

        if (!existing) {
            const newItem: PendingRequest = {
                ...item,
                date_added_utc: nowUtc(),
                date_last_attempt_utc: nowUtc(),
                retry_count: 0,
                in_flight: false,
            }
            queue.push(newItem)
            console.log(`[RequestQueue] ${item.key}: Added request`)
        }

        saveQueue(queue)
    }

    async function sendRequest(item: PendingRequest): Promise<void> {
        const key = item.key
        console.log(`[RequestQueue] Sending request: ${key} (${item.http_method} ${item.url})`)

        // Mark as in-flight before sending
        const queue = getQueue()
        const idx = queue.findIndex(
            (r) => r.key === key
        )
        if (idx !== -1) {
            queue[idx].in_flight = true
            queue[idx].date_last_attempt_utc = nowUtc()
            saveQueue(queue)
        }

        const controller = new AbortController()
        abortControllers.set(key, controller)

        const timeoutId = setTimeout(() => {
            controller.abort()
            console.log(`[RequestQueue] ${key}: Request timed out`)
        }, resolved.requestTimeoutSecs * 1000)

        fetch(`${API_BASE_URL}${item.url}`, {
            method: item.http_method,
            headers: {
                'Content-Type': 'application/json',
                'touchpoints-api-key': API_KEY
            },
            body: JSON.stringify(item.payload),
            signal: controller.signal,
        })
            .then(async (response) => {
                clearTimeout(timeoutId)
                const q = getQueue()
                const i = q.findIndex(
                    (r) => r.key === key
                )
                if (i !== -1) {
                    if (response.status >= 200 && response.status < 300) {
                        // remove the request from the queue on successful completion
                        q.splice(i, 1)
                        console.log(`[RequestQueue] ${key}: Removed request by completion`)
                    } else if (q[i].retry_count >= resolved.retryLimit) {
                        q.splice(i, 1)
                        console.log(`[RequestQueue] ${key}: Removed request by retry limit`)
                    } else {
                        q[i].in_flight = false
                        q[i].retry_count += 1
                        q[i].date_last_attempt_utc = nowUtc()
                        console.log(`[RequestQueue] ${key}: Non-200 response (${response.status}), retry: ${q[i].retry_count}`)
                    }
                    saveQueue(q)
                }
            })
            .catch((err) => {
                clearTimeout(timeoutId)
                const q = getQueue()
                const i = q.findIndex(
                    (r) => r.key === key
                )
                if (i !== -1) {
                    // Handle aborts separately – they are timeouts, not retry failures
                    if (err.name === 'AbortError' || err.message?.includes('aborted')) {
                        q[i].in_flight = false
                        q[i].date_last_attempt_utc = nowUtc()
                        console.log(`[RequestQueue] ${key}: Request timed out – retry count unchanged`)
                    } else {
                        if (q[i].retry_count >= resolved.retryLimit) {
                            q.splice(i, 1)
                            console.log(`[RequestQueue] ${key}: Removed request by retry limit`)
                        } else {
                            q[i].in_flight = false
                            q[i].retry_count += 1
                            q[i].date_last_attempt_utc = nowUtc()
                            console.log(`[RequestQueue] ${key}: Request failed - ${err.message}`)
                        }
                    }
                    saveQueue(q)
                }
            })
            .finally(() => {
                abortControllers.delete(key)
            })
    }

    function prepareQueue(queue: PendingRequest[]): PendingRequest[] {
        // Mark timed-out items as not in-flight (recovery path for stale state after app restart).
        // Skip items that are still in-flight — sendRequest's abort handler will deal with them.
        for (const item of queue) {
            if (item.in_flight && isTimedOut(item, resolved.requestTimeoutSecs)) {
                item.in_flight = false
                console.log(`[RequestQueue] ${item.key}: Timed out in-flight request marked reset`)
            }
        }

        // Remove expired or exceeded-retry items
        let active = queue.filter((item) => {
            if (isExpired(item, resolved.retentionDurationHours)) {
                console.log(`[RequestQueue] ${item.key}: Removed request by expiry`)
                return false
            }
            if (item.retry_count >= resolved.retryLimit) {
                console.log(`[RequestQueue] ${item.key}: Removed request by retry limit`)
                return false
            }
            return true
        })

        // Enforce queue size limit – remove the oldest *non‑in‑flight* items when exceeding maxQueueSize
        const nonFlying = active.filter((r) => !r.in_flight)
        if (nonFlying.length > resolved.maxQueueSize) {
            // Sort non‑flying items by date_added_utc (oldest first)
            const sorted = [...nonFlying].sort((a, b) => new Date(a.date_added_utc).getTime() - new Date(b.date_added_utc).getTime())
            const toRemoveCount = nonFlying.length - resolved.maxQueueSize
            const toRemoveKeys = new Set(sorted.slice(0, toRemoveCount).map((r) => r.key))
            // Keep all in‑flight items and the remaining non‑flying ones
            active = active.filter((r) => r.in_flight || !toRemoveKeys.has(r.key))
            console.log(`[RequestQueue] Removed ${toRemoveCount} requests by queue size limit`)
        }

        return active
    }

    async function processQueue(): Promise<void> {

        // don't process the queue if the client is offline
        // here we perform a quick reachability test to check if the client is online
        // as navigator.onLine may not always be reliable.
        try {
            await fetch(`${API_BASE_URL}`, {
                mode: 'no-cors',
                cache: 'no-store'
            })
        } catch {
            console.log('[RequestQueue] Client is offline - skipping queue processing')
            return Promise.resolve()
        }

        const queue = getQueue()
        const active = await prepareQueue(queue)
        // Persist timeout/expiry/retry mutations before sendRequest re-reads from storage
        saveQueue(active)

        // Send pending requests with a delay between each based on queue size
        // (skip items that just timed out this cycle)
        const delayMs = Math.min(
            resolved.maxDelayMsBetweenRequests,
            Math.floor((resolved.intervalSecs * 1000) / active.length)
        )
        let i = 0
        async function sendNext(): Promise<void> {
            if (i >= active.length) return
            const item = active[i++]
            if (!item.in_flight) {
                await sendRequest(item)
            }
            setTimeout(sendNext, delayMs)
        }
        await sendNext()
    }

    function start(): void {
        if (intervalId) return
        intervalId = setInterval(processQueue, resolved.intervalSecs * 1000)
        console.log(`[RequestQueue] Started with interval: ${resolved.intervalSecs}s`)
    }

    function stop(): void {
        if (intervalId) {
            clearInterval(intervalId)
            intervalId = null
            console.log('[RequestQueue] Stopped')
        }
    }

    return {
        addItem,
        prepareQueue,
        processQueue,
        sendRequest,
        start,
        stop,
    }
}
